import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CarMultiSelect,
  type CarCoverageMap,
  CheckboxDropdown,
  DataState,
  Input,
  PageHeader,
  Select,
  TextArea,
  TrackMultiSelect,
  type TrackCoverageMap,
  buildAutoSubheading,
} from "@vms/ui-web";
import type { BrandingTemplate, Game } from "@vms/types";
import { GAMES } from "@vms/types";
import { useApi } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import {
  useCreateLeaderboard,
  useLeaderboardRecord,
  useUpdateLeaderboard,
} from "../../api/leaderboards";
import { useOrgCars, useOrgEvents, useOrgTracks } from "../../api/orgContent";

interface FormState {
  name: string;
  subheading: string;
  brandingTemplateId: string;
  // venueIds null => all org venues; otherwise explicit subset
  venueIdsMode: "all" | "subset";
  venueIds: string[];
  game: Game;
  dateFrom: string;
  dateTo: string;
  entryLimit: string;
  eventIds: string[];
  tracks: string[];
  cars: string[];
}

const DEFAULT_GAME: Game = GAMES[0];

const EMPTY_FORM: FormState = {
  name: "",
  subheading: "",
  brandingTemplateId: "",
  venueIdsMode: "all",
  venueIds: [],
  game: DEFAULT_GAME,
  dateFrom: "",
  dateTo: "",
  entryLimit: "",
  eventIds: [],
  tracks: [],
  cars: [],
};

/**
 * Create / edit a cross-venue cloud-authored leaderboard. Mirrors hub-ui's
 * `LeaderboardManagementForm` field-for-field, plus the cloud-specific
 * "venue scope" selector. Filter pickers source their data from the
 * `/api/organisations/:id/{tracks,cars,events}` endpoints, narrowed by the
 * current venue scope; coverage badges surface when a track/car is present
 * in fewer venues than the operator's selected scope.
 */
export function OrgLeaderboardForm(): JSX.Element | null {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);

  const { currentOrg, venues } = useOrgContext();
  const api = useApi();

  const { data: existing, loading: loadingExisting, error: existingError } =
    useLeaderboardRecord(id);

  const [brandingTemplates, setBrandingTemplates] = useState<BrandingTemplate[]>([]);
  const [brandingLoading, setBrandingLoading] = useState(false);
  useEffect(() => {
    if (!currentOrg) return;
    let cancelled = false;
    setBrandingLoading(true);
    api(`/api/branding-templates?organisationId=${currentOrg.id}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (j.success) setBrandingTemplates(j.data as BrandingTemplate[]);
      })
      .finally(() => {
        if (!cancelled) setBrandingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, currentOrg]);

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate form from the loaded record on edit. Only once — subsequent
  // record refetches (e.g. WS invalidation) shouldn't clobber unsaved
  // operator edits.
  useEffect(() => {
    if (!isEdit || !existing || hydrated) return;
    if (existing.venueId !== null) {
      // Hub-authored — bail. The list page shouldn't link us here, but
      // belt-and-braces.
      navigate(`/org/${currentOrg?.slug ?? ""}/leaderboards`, { replace: true });
      return;
    }
    setForm({
      name: existing.name,
      subheading: existing.subheading ?? "",
      brandingTemplateId: existing.brandingTemplateId ?? "",
      venueIdsMode: existing.venueIds && existing.venueIds.length > 0 ? "subset" : "all",
      venueIds: existing.venueIds ?? [],
      game: ((existing.game as Game | null) ?? DEFAULT_GAME) as Game,
      dateFrom: existing.dateFrom ?? "",
      dateTo: existing.dateTo ?? "",
      entryLimit: existing.entryLimit != null ? String(existing.entryLimit) : "",
      eventIds: existing.eventIds ?? [],
      tracks: existing.tracks ?? [],
      cars: existing.cars ?? [],
    });
    setHydrated(true);
  }, [isEdit, existing, hydrated, currentOrg, navigate]);

  const create = useCreateLeaderboard();
  const update = useUpdateLeaderboard();

  // Effective venue scope — what the form filter pickers should consult.
  // `null` = all org venues; an array = explicit subset (after we resolve
  // the "all" radio to the full org venue list for coverage math).
  const effectiveVenueIds = useMemo<string[] | null>(() => {
    if (form.venueIdsMode === "subset") return form.venueIds;
    return null;
  }, [form.venueIdsMode, form.venueIds]);

  const totalVenuesInScope =
    form.venueIdsMode === "subset" ? form.venueIds.length : venues.length;

  // Content lookups (narrowed by the venue scope where applicable).
  const { data: orgTracks } = useOrgTracks(
    currentOrg?.id,
    form.game,
    effectiveVenueIds,
  );
  const { data: orgCars } = useOrgCars(
    currentOrg?.id,
    form.game,
    effectiveVenueIds,
  );
  const { data: orgEvents } = useOrgEvents(currentOrg?.id, effectiveVenueIds);

  const trackCoverage = useMemo<TrackCoverageMap | undefined>(() => {
    if (!orgTracks || totalVenuesInScope <= 1) return undefined;
    const byTrackId: TrackCoverageMap["byTrackId"] = {};
    for (const t of orgTracks) {
      byTrackId[t.trackId] = { availableInVenues: t.availableInVenues };
    }
    return { totalVenues: totalVenuesInScope, byTrackId };
  }, [orgTracks, totalVenuesInScope]);

  const carCoverage = useMemo<CarCoverageMap | undefined>(() => {
    if (!orgCars || totalVenuesInScope <= 1) return undefined;
    const byCarId: CarCoverageMap["byCarId"] = {};
    for (const c of orgCars) {
      byCarId[c.carId] = { availableInVenues: c.availableInVenues };
    }
    return { totalVenues: totalVenuesInScope, byCarId };
  }, [orgCars, totalVenuesInScope]);

  const venueOptions = useMemo(
    () => venues.map((v) => ({ value: v.id, label: v.name })),
    [venues],
  );
  const brandingOptions = useMemo(
    () => [
      { value: "", label: "Default (none)" },
      ...brandingTemplates.map((b) => ({ value: b.id, label: b.name })),
    ],
    [brandingTemplates],
  );

  function setField<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((f) => ({ ...f, [k]: v }));
  }

  function handleGameChange(newGame: Game) {
    // Clear game-specific selections when game changes — same behaviour as
    // hub-ui's form (different game ⇒ different track / car IDs).
    setForm((f) => ({
      ...f,
      game: newGame,
      tracks: [],
      cars: [],
    }));
  }

  // Auto-populate the subheading from active filters whenever a filter
  // changes AND the field is currently empty. Identical semantics to
  // hub-ui's form (see `buildAutoSubheading` in @vms/ui-web).
  useEffect(() => {
    if (form.subheading.trim() !== "") return;
    const auto = buildAutoSubheading({
      tracks: form.tracks,
      cars: form.cars,
      game: form.game,
      eventIds: form.eventIds,
      dateFrom: form.dateFrom,
      dateTo: form.dateTo,
      allTracks: orgTracks,
      allCars: orgCars,
    });
    if (auto) setForm((f) => ({ ...f, subheading: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally exclude `form.subheading`
  }, [
    form.tracks,
    form.cars,
    form.game,
    form.eventIds,
    form.dateFrom,
    form.dateTo,
    orgTracks,
    orgCars,
  ]);

  const eventOptions = useMemo(() => {
    if (!orgEvents) return [];
    return [...orgEvents]
      .sort((a, b) => {
        const at = a.startDate ? new Date(a.startDate).getTime() : 0;
        const bt = b.startDate ? new Date(b.startDate).getTime() : 0;
        return bt - at;
      })
      .map((e) => {
        const trackName = e.trackName ?? e.track ?? "";
        const carNames = [
          ...new Set((e.carNames ?? e.carsAvailable ?? []).map((c) => c.split(" / ")[0])),
        ].join(", ");
        const startDate = e.startDate
          ? new Date(e.startDate).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "";
        const subtitle = [startDate, trackName, carNames, e.venueName]
          .filter(Boolean)
          .join(" • ");
        return { value: e.id, label: e.name, subtitle };
      });
  }, [orgEvents]);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!currentOrg) return;
    if (!form.name.trim()) {
      alert("Name is required");
      return;
    }
    if (form.venueIdsMode === "subset" && form.venueIds.length === 0) {
      alert("Pick at least one venue, or switch to 'All venues'.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      subheading: form.subheading.trim() || null,
      brandingTemplateId: form.brandingTemplateId || null,
      venueIds: form.venueIdsMode === "all" ? null : form.venueIds,
      game: form.game,
      dateFrom: form.dateFrom || null,
      dateTo: form.dateTo || null,
      entryLimit: form.entryLimit ? parseInt(form.entryLimit, 10) : null,
      eventIds: form.eventIds,
      tracks: form.tracks,
      cars: form.cars,
    };

    try {
      if (isEdit && id) {
        await update.mutate({ id, ...payload });
      } else {
        await create.mutate({ organisationId: currentOrg.id, ...payload });
      }
      navigate(`/org/${currentOrg.slug}/leaderboards`);
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  if (!currentOrg) return null;

  const loading = create.loading || update.loading;

  return (
    <div className="space-y-4">
      <PageHeader
        title={isEdit ? "Edit leaderboard" : "New leaderboard"}
        subtitle="Cross-venue leaderboard. Lives only on the cloud — never synced to a hub."
      />
      <DataState
        loading={isEdit && loadingExisting}
        error={existingError}
        empty={false}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Top row: Basic Info + Branding */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Info</h2>
              <div className="space-y-4">
                <Input
                  label="Name"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder='e.g. "FPZERO Spring Championship"'
                  required
                />
                <div>
                  <TextArea
                    label="Subheading"
                    value={form.subheading}
                    onChange={(e) => setField("subheading", e.target.value)}
                    placeholder="Auto-filled from filters"
                    rows={2}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Shown under the leaderboard name on the display. Auto-fills from the
                    active filters when blank — clear it to hide.
                  </p>
                </div>
                <Select
                  label="Game"
                  value={form.game}
                  onChange={(e) => handleGameChange(e.target.value as Game)}
                  options={[
                    { value: "assetto-corsa", label: "Assetto Corsa" },
                    { value: "rfactor-2", label: "rFactor 2" },
                  ]}
                />
              </div>
            </Card>

            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Branding</h2>
              <div className="space-y-4">
                <Select
                  label="Branding template"
                  value={form.brandingTemplateId}
                  onChange={(e) => setField("brandingTemplateId", e.target.value)}
                  disabled={brandingLoading}
                  options={brandingOptions}
                />
              </div>
            </Card>
          </div>

          {/* Venue scope */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Venue scope</h2>
            <p className="text-sm text-gray-500 mb-4">
              Which venues' lap times feed this leaderboard.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="venueScope"
                  checked={form.venueIdsMode === "all"}
                  onChange={() => setField("venueIdsMode", "all")}
                />
                <span>
                  All {currentOrg.name} venues ({venues.length})
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="venueScope"
                  checked={form.venueIdsMode === "subset"}
                  onChange={() => setField("venueIdsMode", "subset")}
                />
                <span>Selected venues</span>
              </label>
            </div>
            {form.venueIdsMode === "subset" && (
              <div className="mt-3">
                <CheckboxDropdown
                  label={
                    form.venueIds.length === 0
                      ? "Pick venues…"
                      : `${form.venueIds.length} venue${form.venueIds.length === 1 ? "" : "s"} selected`
                  }
                  options={venueOptions}
                  selected={form.venueIds}
                  onChange={(s) => setField("venueIds", s)}
                  searchable={venueOptions.length > 8}
                />
              </div>
            )}
          </Card>

          {/* Filters — full width, two columns */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Filters</h2>
            <p className="text-sm text-gray-500 mb-4">
              Leave empty to include all data. Add filters to narrow the leaderboard.
              {totalVenuesInScope > 1 && (
                <>
                  {" "}
                  Tracks / cars that aren't present at every selected venue show an
                  amber coverage hint.
                </>
              )}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Events
                  </label>
                  <CheckboxDropdown
                    label="Select Events"
                    searchable
                    searchPlaceholder="Search events…"
                    options={eventOptions}
                    selected={form.eventIds}
                    onChange={(s) => setField("eventIds", s)}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Valid From"
                    type="date"
                    value={form.dateFrom}
                    onChange={(e) => setField("dateFrom", e.target.value)}
                  />
                  <Input
                    label="Valid To"
                    type="date"
                    value={form.dateTo}
                    onChange={(e) => setField("dateTo", e.target.value)}
                  />
                </div>

                <div>
                  <Input
                    label="Entry Limit"
                    type="number"
                    min={1}
                    value={form.entryLimit}
                    onChange={(e) => setField("entryLimit", e.target.value)}
                    placeholder="100 (default)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum number of drivers shown on the leaderboard. Each driver
                    appears once with their fastest lap. Leave blank to use the default
                    of 100.
                  </p>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tracks
                  </label>
                  <TrackMultiSelect
                    tracks={orgTracks}
                    value={form.tracks}
                    onChange={(s) => setField("tracks", s)}
                    coverage={trackCoverage}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cars
                  </label>
                  <CarMultiSelect
                    cars={orgCars}
                    value={form.cars}
                    onChange={(s) => setField("cars", s)}
                    coverage={carCoverage}
                  />
                </div>
              </div>
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={loading}>
              {isEdit ? "Save changes" : "Create leaderboard"}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate(`/org/${currentOrg.slug}/leaderboards`)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DataState>
    </div>
  );
}
